import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { share, tap } from 'rxjs/operators';

import { ApPersistenceService } from '@ap/persistence';

import { CacheData, ExistingObservable } from './mijn-interface-cache.model';
import { Parameters } from './mijn-interface-cache.types';

@Injectable({ providedIn: 'root' })
export class MijnInterfaceCacheService {
    private readonly httpClient: HttpClient = inject(HttpClient);
    private readonly persistence: ApPersistenceService = inject(ApPersistenceService);

    private readonly CACHE_KEY = 'mijn-cache';
    private existingObservables: Array<ExistingObservable> = [];

    public getData<T>(baseUrl: string, path: string, ttl = -1, params?: Parameters, headers?: HttpHeaders): Observable<T> {
        if (headers?.get('Cache-Control') !== 'no-cache') {
            const cachedData = this.getCachedData<T>(baseUrl, path, ttl, params);
            if (!!cachedData) {
                return of(cachedData);
            }
        }

        const existingObservables = this.findExistingObservablesByIdAndParams(`${baseUrl}/${path}`, params?.toString());
        if (existingObservables.length) {
            return existingObservables[0].obs;
        }

        const options: { params?: Parameters; headers?: HttpHeaders } = {};
        if (params) {
            options.params = params;
        }
        if (headers) {
            options.headers = headers;
        }

        const newObservable = this.httpClient.get<T>(`${baseUrl}/${path}`, options).pipe(
            share(),
            tap((data) => {
                this.setData(baseUrl, path, data, params);

                for (let i = this.existingObservables.length - 1; i >= 0; i--) {
                    if (this.existingObservables[i].id === `${baseUrl}/${path}`) {
                        this.existingObservables.splice(i, 1);
                    }
                }
            })
        );
        this.existingObservables.push({ obs: newObservable, id: `${baseUrl}/${path}`, params: params?.toString() });
        return newObservable;
    }

    public clearCache(baseUrl: string, path?: string): void {
        if (!!path) {
            let cachedData: CacheData[] = this.persistence.getSession(this.CACHE_KEY, baseUrl) ?? [];
            cachedData = cachedData.filter((cached) => cached.path !== path);
            this.persistence.setSession(this.CACHE_KEY, baseUrl, cachedData);
        } else {
            this.persistence.setSession(this.CACHE_KEY, baseUrl, null);
        }
    }

    public removeCompleteCache(): void {
        this.persistence.clearSession(this.CACHE_KEY);
    }

    private setData(baseUrl: string, path: string, data: any, httpParams: Parameters | undefined): void {
        const cachedData: CacheData[] = this.persistence.getSession(this.CACHE_KEY, baseUrl) ?? [];
        cachedData.forEach((cacheData, index) => {
            if (cacheData.path === path && cacheData.params === httpParams?.toString()) {
                cachedData.splice(index, 1);
            }
        });

        cachedData.push({ path, data, time: Date.now(), params: httpParams?.toString() });

        this.persistence.setSession(this.CACHE_KEY, baseUrl, cachedData);
    }

    private getCachedData<T>(baseUrl: string, path: string, ttl: number, httpParams?: Parameters): T | null {
        const cachedData: CacheData[] = this.persistence.getSession(this.CACHE_KEY, baseUrl);
        const cacheData = cachedData?.find((data) => data.path === path && data.params === httpParams?.toString());

        if (cacheData && this.isWithinTtl(cacheData.time, ttl)) {
            return cacheData.data as T;
        }

        return null;
    }

    private isWithinTtl(cacheTime: number, ttl: number): boolean {
        return ttl < 0 ? true : cacheTime + ttl * 1000 > Date.now();
    }

    private findExistingObservablesByIdAndParams(id: string, params: string | undefined): ExistingObservable[] {
        return this.existingObservables.filter((obs) => obs.id === id && obs.params === params);
    }
}
